import React, { useState, useEffect } from 'react';
import { Mail, Send, User, Building2, Phone, ArrowLeft, Crown, Check, ShieldCheck, Users, Cloud, Star, Loader2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { DodoPayments } from 'dodopayments-checkout';

const PremiumPageMonthly = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', surname: '', company: '', email: '', phone: '', package: 'PREMIUM_MONTHLY'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // TEST MODE — Premium Monthly još nije odobren za live na Dodo strani.
    // NE MIJENJAJ u 'live' dok Dodo ne potvrdi da je proizvod aktivan.
    DodoPayments.Initialize({
      mode: 'test',
      displayType: 'overlay',
      onEvent: (event) => {
        console.log('Dodo event (TEST/Premium Monthly):', event);
        switch (event.event_type) {
          case 'checkout.opened':
            setIsLoading(false);
            break;
          case 'checkout.closed':
            setIsLoading(false);
            break;
          case 'checkout.error':
            setIsLoading(false);
            setErrorMessage(t('premium_payments.step2.error_checkout'));
            console.error('Dodo checkout error (TEST):', event.data?.message);
            break;
          case 'checkout.link_expired':
            setIsLoading(false);
            setErrorMessage(t('premium_payments.step2.error_expired'));
            break;
          default:
            break;
        }
      },
    });
  }, [t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
  };

  // ── KORAK 1: PROVJERA EMAILA ────────────────────────────────────────────
  const handleNextStep = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) return;

    setIsLoading(true);
    setErrorMessage('');
    try {
      const { error } = await supabase.functions.invoke('premium-registration-monthly', {
        body: { email: formData.email, check_only: true },
      });

      if (error) {
        let errorMsg = t('premium_payments.step1.error_generic');
        try {
          const errDetails = await error.context.json();
          if (errDetails.error === 'EMAIL_EXISTS') {
            errorMsg = t('premium_payments.step1.error_email_exists');
          } else {
            errorMsg = errDetails.error || error.message;
          }
        } catch (e) {
          errorMsg = error.message || t('premium_payments.step1.error_generic');
        }
        setErrorMessage(errorMsg);
        return;
      }

      setStep(2);
    } catch (err) {
      setErrorMessage(t('premium_payments.step1.error_server'));
    } finally {
      setIsLoading(false);
    }
  };

  // ── KORAK 2: OTVARANJE DODO OVERLAY-A (TEST MODE) ──────────────────────
  const handleOpenDodoCheckout = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      sessionStorage.setItem('dodo_form_data', JSON.stringify(formData));

      const { data, error } = await supabase.functions.invoke('create-dodo-checkout-premium-monthly', {
        body: {
          name: formData.name,
          surname: formData.surname,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          package: formData.package,
        },
      });

      if (error || !data?.checkoutUrl) {
        throw new Error(error?.message || t('premium_payments.step2.error_open'));
      }

      await DodoPayments.Checkout.open({
        checkoutUrl: data.checkoutUrl,
        options: {
          showTimer: true,
          showSecurityBadge: true,
        },
      });
    } catch (err) {
      console.error('Dodo checkout error (TEST/Premium Monthly):', err);
      setErrorMessage(t('premium_payments.step2.error_open'));
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8 group bg-transparent border-none cursor-pointer p-0 transition-colors"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {t('premium_payments.back')}
        </button>

        {/* PREMIUM MONTHLY HEADER */}
        <div className="bg-gradient-to-br from-amber-500/10 to-primary/5 border-2 border-amber-500/20 rounded-[2.5rem] p-8 mb-12 relative overflow-hidden shadow-sm">
          <div className="absolute top-0 right-0 p-8 opacity-10"><Crown size={120} /></div>
          <div className="relative z-10 space-y-4 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 text-amber-600">
              <Crown fill="currentColor" size={24} />
              <span className="font-black uppercase tracking-widest text-xs">{t('premium_payments.badge')}</span>
            </div>
            {/* CIJENA: koristi price_monthly / price_period_monthly */}
            <h1 className="text-4xl font-black uppercase tracking-tighter">
              {t('premium_payments.price_monthly')}{' '}
              <span className="text-lg not-italic text-muted-foreground tracking-normal font-bold">
                {t('premium_payments.price_period_monthly')}
              </span>
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-amber-500/10">
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tight">
                <Users size={16} className="text-primary" /> {t('premium_payments.feature_employees')}
              </div>
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tight">
                <Cloud size={16} className="text-primary" /> {t('premium_payments.feature_cloud')}
              </div>
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tight">
                <ShieldCheck size={16} className="text-primary" /> {t('premium_payments.feature_audit')}
              </div>
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tight">
                <Star size={16} className="text-primary" /> {t('premium_payments.feature_support')}
              </div>
            </div>
          </div>
        </div>

        {step === 2 ? (
          /* STEP 2: DODO PAYMENT + RIEPILOGO */
          <div className="bg-card p-10 rounded-[2.5rem] border border-border shadow-xl space-y-8 animate-in fade-in slide-in-from-bottom-4">
            {isLoading ? (
              <div className="text-center py-12 space-y-6">
                <div className="relative w-20 h-20 mx-auto">
                  <Loader2 className="w-20 h-20 text-primary animate-spin" />
                  <Crown className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-primary" size={24} />
                </div>
                <p className="text-sm font-black uppercase tracking-widest text-primary animate-pulse italic">
                  {t('premium_payments.step2.loading')}
                </p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-end border-b border-border pb-8">
                  <div>
                    <h3 className="text-xl font-black uppercase leading-none mb-1 text-primary">
                      {t('premium_payments.step2.title')}
                    </h3>
                    <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider italic">
                      {t('premium_payments.step2.subtitle')} {formData.company}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground font-black uppercase">
                      {t('premium_payments.step2.total_label')}
                    </p>
                    {/* IZNOS: total_amount_monthly (ne total_amount) */}
                    <p className="text-3xl font-black text-foreground leading-none">
                      {t('premium_payments.step2.total_amount_monthly')}
                    </p>
                  </div>
                </div>

                <div className="bg-background border border-border rounded-3xl p-6 space-y-3 relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-4 text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground border border-border rounded-full whitespace-nowrap">
                    {t('premium_payments.step2.summary_title')}
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[10px] text-muted-foreground font-black uppercase">{t('premium_payments.step2.summary_name')}:</span>
                    <span className="text-[10px] font-bold">{formData.name} {formData.surname}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-muted-foreground font-black uppercase">{t('premium_payments.step2.summary_company')}:</span>
                    <span className="text-[10px] font-bold">{formData.company}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-muted-foreground font-black uppercase">{t('premium_payments.step2.summary_email')}:</span>
                    <span className="text-[10px] font-bold text-primary">{formData.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-muted-foreground font-black uppercase">{t('premium_payments.step2.summary_phone')}:</span>
                    <span className="text-[10px] font-bold">{formData.phone || '—'}</span>
                  </div>
                  {/* TRAJANJE: summary_package_value_monthly ("Premium — 30 dana") */}
                  <div className="flex justify-between items-center pt-3 border-t border-dashed border-border">
                    <span className="text-[10px] text-muted-foreground font-black uppercase">{t('premium_payments.step2.summary_package')}:</span>
                    <span className="bg-amber-500/10 text-amber-700 text-[10px] font-black px-3 py-1 rounded-full uppercase">
                      {t('premium_payments.step2.summary_package_value_monthly')}
                    </span>
                  </div>
                </div>

                <div className="max-w-md mx-auto py-2 space-y-4">
                  {/* DUGME PLATI: btn_pay_monthly (ispravno napisano, bez tipfelera, za razliku od basic) */}
                  <button
                    onClick={handleOpenDodoCheckout}
                    disabled={isLoading}
                    className="w-full bg-primary text-white py-5 rounded-2xl font-black uppercase tracking-[0.15em] text-sm shadow-xl shadow-primary/25 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShieldCheck size={20} />
                    {t('premium_payments.step2.btn_pay_monthly')}
                  </button>
                  <p className="text-center text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                    {t('premium_payments.step2.ssl_label')}
                  </p>
                </div>

                <button
                  onClick={() => setStep(1)}
                  className="w-full text-[10px] font-black text-muted-foreground uppercase tracking-widest hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-2 underline underline-offset-4"
                >
                  {t('premium_payments.step2.btn_back')}
                </button>
              </>
            )}

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-4 rounded-2xl text-xs font-black text-center flex items-center justify-center gap-2">
                <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-ping" />
                {errorMessage}
              </div>
            )}
          </div>
        ) : (
          /* STEP 1: FORMA */
          <section className="bg-card p-8 md:p-12 rounded-[2.5rem] border border-border shadow-sm relative overflow-hidden animate-in fade-in duration-500">
            <div className="mb-10 text-center md:text-left">
              <h3 className="text-xl font-black uppercase mb-1 tracking-tight">{t('premium_payments.step1.title')}</h3>
              <p className="text-xs text-muted-foreground uppercase italic tracking-[0.1em]">{t('premium_payments.step1.subtitle')}</p>
            </div>

            <form onSubmit={handleNextStep} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2 italic">{t('premium_payments.step1.label_name')}</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                    <input required type="text" name="name" value={formData.name} onChange={handleChange} placeholder={t('premium_payments.step1.placeholder_name')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-medium" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2 italic">{t('premium_payments.step1.label_surname')}</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                    <input required type="text" name="surname" value={formData.surname} onChange={handleChange} placeholder={t('premium_payments.step1.placeholder_surname')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-medium" />
                  </div>
                </div>
                <div className="space-y-2 col-span-full">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2 italic">{t('premium_payments.step1.label_company')}</label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                    <input required type="text" name="company" value={formData.company} onChange={handleChange} placeholder={t('premium_payments.step1.placeholder_company')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-medium" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2 italic">{t('premium_payments.step1.label_email')}</label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                    <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('premium_payments.step1.placeholder_email')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-medium" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2 italic">{t('premium_payments.step1.label_phone')}</label>
                  <div className="relative group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                    <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('premium_payments.step1.placeholder_phone')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-medium" />
                  </div>
                </div>
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-4 rounded-2xl text-xs font-black text-center animate-shake flex items-center justify-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-ping" />
                  {errorMessage}
                </div>
              )}

              <div className="flex items-start gap-3 px-2 py-2">
                <div className="relative flex items-center justify-center shrink-0">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border-2 border-border bg-background transition-all checked:border-primary checked:bg-primary"
                  />
                  <Check className="absolute h-4 w-4 text-white opacity-0 transition-opacity peer-checked:opacity-100 pointer-events-none" strokeWidth={4} />
                </div>
                <label htmlFor="terms" className="text-sm text-muted-foreground font-medium cursor-pointer select-none pt-0.5">
                  {t('premium_payments.step1.terms_part1')}{' '}
                  <Link to="/legal-page" className="text-primary font-bold hover:underline underline-offset-4 transition-all">
                    {t('premium_payments.step1.terms_link')}
                  </Link>{' '}
                  {t('premium_payments.step1.terms_part2')}
                </label>
              </div>

              <button
                disabled={isLoading || !acceptedTerms}
                className={`w-full py-5 rounded-2xl font-black uppercase tracking-[0.2em] shadow-xl flex justify-center items-center gap-3 text-xs transition-all ${
                  isLoading || !acceptedTerms
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-primary text-white hover:brightness-110 active:scale-95 shadow-primary/25'
                }`}
              >
                {isLoading ? <Loader2 className="animate-spin" size={18} /> : <>{t('premium_payments.step1.btn_next')} <Send size={16} /></>}
              </button>
            </form>
          </section>
        )}

        <div className="mt-12 text-center border-t border-border pt-8 flex flex-col items-center gap-4">
          <div className="flex items-center gap-6 opacity-30">
            <ShieldCheck size={16} className="text-muted-foreground" />
            <div className="h-4 w-[1px] bg-muted-foreground"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{t('premium_payments.footer_ssl')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumPageMonthly;